import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Chip,
    Stack,
    Alert,
    IconButton,
    Divider,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RestaurantIcon from '@mui/icons-material/Restaurant';

export default function RestauLocationMappingDialog({
    open,
    onClose,
    onSave,
    posType, // 'square' or 'clover'
    posLocations, // Array of POS locations from OAuth
    restauIdsWithAccess,
    existingRestauPosMappings, // Array of existing mappings to exclude
    merchantId, // Square/Clover merchant ID
    existingMappings, // Array of existing mappings for this merchant - for editing all mappings
}) {
    const [availableRestauIds, setAvailableRestauIds] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [mappings, setMappings] = useState([]); // Array of {restauId, location}
    const [selectedRestauId, setSelectedRestauId] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            // Extract already mapped restauIds from all merchants
            const mappedRestauIds = new Set();
            const mappedLocationIds = new Set();

            // Create a set of restauIds and locationIds being edited (to exclude from "already mapped")
            const editingRestauIds = new Set();
            const editingLocationIds = new Set();

            if (existingMappings && existingMappings.length > 0) {
                existingMappings.forEach((mapping) => {
                    editingRestauIds.add(mapping.restauId);
                    editingLocationIds.add(mapping.locationId);
                });
            }

            if (existingRestauPosMappings) {
                Object.values(existingRestauPosMappings).forEach(
                    (merchantData) => {
                        merchantData.restauMappings.forEach((mappingObj) => {
                            Object.entries(mappingObj).forEach(
                                ([restauId, locationId]) => {
                                    // When editing, exclude the mappings being edited from "already mapped" set
                                    if (
                                        editingRestauIds.has(restauId) ||
                                        editingLocationIds.has(locationId)
                                    ) {
                                        return; // Skip this mapping
                                    }
                                    mappedRestauIds.add(restauId);
                                    mappedLocationIds.add(locationId);
                                }
                            );
                        });
                    }
                );
            }

            // Filter out already mapped restaurants
            const available = Object.keys(restauIdsWithAccess || {})
                .filter((r) => !mappedRestauIds.has(r))
                .map((x) => {
                    return { id: x, name: restauIdsWithAccess[x] };
                });
            setAvailableRestauIds(available);

            // Filter out already mapped locations
            const availableLocs = (posLocations || []).filter(
                (l) => !mappedLocationIds.has(l.id)
            );
            setAvailableLocations(availableLocs);

            // If editing all mappings, pre-populate with existing mappings
            if (existingMappings && existingMappings.length > 0) {
                const preMappings = existingMappings
                    .map((mapping) => {
                        const restau = available.find(
                            (r) => r.id === mapping.restauId
                        );
                        const loc = availableLocs.find(
                            (l) => l.id === mapping.locationId
                        );

                        if (restau && loc) {
                            return { restauId: restau, location: loc };
                        }
                        return null;
                    })
                    .filter((m) => m !== null);

                setMappings(preMappings);
            } else {
                setMappings([]);
            }

            setSelectedRestauId(null);
            setSelectedLocation(null);
        }
    }, [
        open,
        posLocations,
        existingRestauPosMappings,
        restauIdsWithAccess,
        existingMappings,
    ]);

    const handleAddMapping = () => {
        if (!selectedRestauId || !selectedLocation) {
            alert('Please select both a restaurant and a location');
            return;
        }

        // Check if restauId or location is already mapped
        const restauAlreadyMapped = mappings.some(
            (m) => m.restauId.id === selectedRestauId.id
        );
        const locationAlreadyMapped = mappings.some(
            (m) => m.location.id === selectedLocation.id
        );

        if (restauAlreadyMapped || locationAlreadyMapped) {
            alert('This restaurant or location is already mapped');
            return;
        }

        setMappings([
            ...mappings,
            { restauId: selectedRestauId, location: selectedLocation },
        ]);
        setSelectedRestauId(null);
        setSelectedLocation(null);
    };

    const handleRemoveMapping = (index) => {
        setMappings(mappings.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        //debugger;
        if (mappings.length === 0) {
            alert('Please create at least one mapping');
            return;
        }

        setSaving(true);

        try {
            // Calculate which mappings were deleted and which were added
            const deletedMappings = [];
            const addedMappings = [];
            const unchangedMappings = [];

            // If we have existing mappings (editing mode), calculate the diff
            if (existingMappings && existingMappings.length > 0) {
                // Find deleted mappings (in existingMappings but not in current mappings)
                existingMappings.forEach((existing) => {
                    const stillExists = mappings.find(
                        (m) =>
                            m.restauId.id === existing.restauId &&
                            m.location.id === existing.locationId
                    );
                    if (!stillExists) {
                        deletedMappings.push({
                            restauId: existing.restauId,
                            locationId: existing.locationId,
                        });
                    }
                });

                // Find added mappings (in current mappings but not in existingMappings)
                mappings.forEach((current) => {
                    const wasExisting = existingMappings.find(
                        (e) =>
                            e.restauId === current.restauId.id &&
                            e.locationId === current.location.id
                    );
                    if (wasExisting) {
                        unchangedMappings.push(current);
                    } else {
                        addedMappings.push(current);
                    }
                });
            } else {
                // No existing mappings, all current mappings are new
                addedMappings.push(...mappings);
            }

            // Pass all information to the parent and wait for completion
            await onSave({
                allMappings: mappings,
                addedMappings,
                deletedMappings,
                unchangedMappings,
                merchantId,
            });
        } finally {
            setSaving(false);
        }
    };

    const getAvailableRestauIds = () => {
        const mappedIds = mappings.map((m) => m.restauId.id);
        return availableRestauIds.filter((r) => !mappedIds.includes(r.id));
    };

    const getAvailableLocations = () => {
        const mappedIds = mappings.map((m) => m.location.id);
        return availableLocations.filter((l) => !mappedIds.includes(l.id));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">
                        Map Restaurants to{' '}
                        {posType === 'square' ? 'Square' : 'Clover'} Locations
                    </Typography>
                </Box>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    Create mappings between your restaurants and{' '}
                    {posType === 'square' ? 'Square' : 'Clover'} locations
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Each restaurant can only be mapped to one location, and each
                    location can only have one restaurant mapped to it.
                </Alert>

                {/* Selection Area */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 'bold' }}
                    >
                        Create New Mapping
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        {/* Restaurant Selection */}
                        <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Select Restaurant
                            </Typography>
                            <List
                                dense
                                sx={{ maxHeight: 200, overflow: 'auto' }}
                            >
                                {getAvailableRestauIds().length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ p: 2, textAlign: 'center' }}
                                    >
                                        No restaurants available
                                    </Typography>
                                ) : (
                                    getAvailableRestauIds().map((restau) => (
                                        <ListItem
                                            key={restau.id}
                                            disablePadding
                                        >
                                            <ListItemButton
                                                selected={
                                                    selectedRestauId?.id ===
                                                    restau.id
                                                }
                                                onClick={() =>
                                                    setSelectedRestauId(restau)
                                                }
                                            >
                                                <RestaurantIcon
                                                    sx={{ mr: 1, fontSize: 20 }}
                                                />
                                                <ListItemText
                                                    primary={restau.name}
                                                    secondary={`ID: ${restau.id}`}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))
                                )}
                            </List>
                        </Paper>

                        {/* Location Selection */}
                        <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Select{' '}
                                {posType === 'square' ? 'Square' : 'Clover'}{' '}
                                Location
                            </Typography>
                            <List
                                dense
                                sx={{ maxHeight: 200, overflow: 'auto' }}
                            >
                                {getAvailableLocations().length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ p: 2, textAlign: 'center' }}
                                    >
                                        No locations available
                                    </Typography>
                                ) : (
                                    getAvailableLocations().map((location) => (
                                        <ListItem
                                            key={location.id}
                                            disablePadding
                                        >
                                            <ListItemButton
                                                selected={
                                                    selectedLocation?.id ===
                                                    location.id
                                                }
                                                onClick={() =>
                                                    setSelectedLocation(
                                                        location
                                                    )
                                                }
                                            >
                                                <LocationOnIcon
                                                    sx={{ mr: 1, fontSize: 20 }}
                                                />
                                                <ListItemText
                                                    primary={location.name}
                                                    secondary={`ID: ${location.id.slice(
                                                        0,
                                                        12
                                                    )}...`}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))
                                )}
                            </List>
                        </Paper>
                    </Stack>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddMapping}
                        disabled={!selectedRestauId || !selectedLocation}
                        fullWidth
                    >
                        Add Mapping
                    </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Current Mappings */}
                <Box>
                    <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 'bold' }}
                    >
                        Current Mappings ({mappings.length})
                    </Typography>
                    {mappings.length === 0 ? (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                backgroundColor: 'grey.50',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                No mappings created yet. Add your first mapping
                                above.
                            </Typography>
                        </Paper>
                    ) : (
                        <List>
                            {mappings.map((mapping, index) => (
                                <Paper
                                    key={index}
                                    variant="outlined"
                                    sx={{ mb: 1, p: 2 }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ flex: 1 }}
                                        >
                                            <Box
                                                sx={{
                                                    flex: 0.7,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Chip
                                                    icon={<RestaurantIcon />}
                                                    label={
                                                        mapping.restauId.name
                                                    }
                                                    size="small"
                                                    sx={{ mb: 0.5 }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    color="text.secondary"
                                                    sx={{ textAlign: 'center' }}
                                                >
                                                    {mapping.restauId.id}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                color="text.secondary"
                                            >
                                                →
                                            </Typography>
                                            <Box
                                                sx={{
                                                    flex: 1.5,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Chip
                                                    icon={<LocationOnIcon />}
                                                    label={
                                                        mapping.location.name
                                                    }
                                                    color="primary"
                                                    size="small"
                                                    sx={{ mb: 0.5 }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    color="text.secondary"
                                                    sx={{ textAlign: 'center' }}
                                                >
                                                    {mapping.location.id.slice(
                                                        0,
                                                        20
                                                    )}
                                                    ...
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                handleRemoveMapping(index)
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                </Paper>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={mappings.length === 0 || saving}
                    startIcon={
                        saving ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : null
                    }
                >
                    {saving
                        ? 'Saving...'
                        : `Save ${mappings.length} Mapping${
                              mappings.length !== 1 ? 's' : ''
                          }`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
